import { calculateAgeForTest, displayPatientName, formatLongDate } from "../../../utils/CommonUtil";
import { useEffect, useState } from "react";

import { useSearchParams } from "react-router-dom";
import useTestList from "../../../hooks/useTestList";

export interface PatientData {
  name: string;
  age: number;
  testDate: string;
  testId: string;
}

export interface ConductedTests {
  visionScreening: boolean;
  neurologicalAssessment: boolean;
  lazyEyeScreening: boolean;
}

export interface ResultsSummaryTestIds {
  uuid?: string;
  visionUuid?: string;
  neurologyUuid?: string;
  lazyEyeUuid?: string;
}

export function useResultsSummaryData(overrideTestIds?: ResultsSummaryTestIds | null) {
  const [searchParams] = useSearchParams();
  const uuid = overrideTestIds?.uuid ?? searchParams.get("uuid");
  const visionUuid = overrideTestIds?.visionUuid ?? searchParams.get("visionUuid");
  const neurologyUuid = overrideTestIds?.neurologyUuid ?? searchParams.get("neurologyUuid");
  const lazyEyeUuid = overrideTestIds?.lazyEyeUuid ?? searchParams.get("lazyEyeUuid");

  // Use any available UUID to fetch patient data
  const primaryUuid = uuid || visionUuid || neurologyUuid || lazyEyeUuid;

  const { testList, testListLoading } = useTestList(primaryUuid ? { uuid: primaryUuid } : {});

  const [patientData, setPatientData] = useState<PatientData>({
    name: "Patient",
    age: 0,
    testDate: formatLongDate(new Date()),
    testId: "RS-2025-0000",
  });

  // Track which tests were conducted based on UUID params
  const conductedTests: ConductedTests = {
    visionScreening: !!visionUuid || !!uuid,
    neurologicalAssessment: !!neurologyUuid,
    lazyEyeScreening: !!lazyEyeUuid,
  };

  useEffect(() => {
    let tests: any[] = [];

    if (Array.isArray(testList)) {
      tests = testList;
    } else if (
      testList &&
      typeof testList === "object" &&
      "data" in testList &&
      Array.isArray((testList as any).data)
    ) {
      tests = (testList as any).data;
    }

    if (tests.length > 0) {
      const test = tests[0];

      const patientAge = test.patient?.dob
        ? calculateAgeForTest(new Date(test.startDate), new Date(test.patient.dob))
        : 0;

      const testDate = new Date(test.startDate);
      const testId = `RS-${testDate.getFullYear()}-${String(testDate.getMonth() + 1).padStart(2, "0")}${String(testDate.getDate()).padStart(2, "0")}`;

      setPatientData({
        name: test.patient?.name ? displayPatientName(test.patient.name) : "Patient",
        age: patientAge,
        testDate: formatLongDate(testDate),
        testId,
      });
    }
  }, [testList]);

  return {
    patientData,
    conductedTests,
    loading: testListLoading,
  };
}
